import { HomeGreeting } from '@/components/screens/HomePage/HomeGreetingProps';
import { linkoutConfigs } from '@/config/routing/LinkoutConfig';
import {
  COMMUNITY_CARD_BACKGROUND,
  HOME_POST_RNR_BACKGROUND,
  REFERAL_CARD_BACKGROUND,
} from '@/constants/images';
import { CommunityCard } from '@screens/HomePage/ResultReady/CommunityCard';
import { HealthResultCard } from '@screens/HomePage/ResultReady/HealthResultCard';
import { HealthSummaryCards } from '@screens/HomePage/ResultReady/HealthSummaryCards';
import { HomePageFormulaSection } from '@screens/HomePage/ResultReady/HomePageFormulaSection';
import { MyNutritionCard } from '@screens/HomePage/ResultReady/MyNutritionCard';
import { NutritionEssentials } from '@screens/HomePage/ResultReady/NutritionEssentials';
import { MyRecipesSection } from '@screens/NutritionPage/MyRecipesSection';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';

import { Render } from '@/components/Helpers/ComponentRender/Render';
import { Skeleton } from '@/components/design-system/Skeleton';
import { NotificationCenter } from '@/components/layouts/Notification/NotificationCenter';
import {
  getTimeBasedGreetingAndBgImg,
  isRecentlyPublished,
} from '@/components/utils/Timezone';
import { useBootup } from '@/hooks/useBootup';
import { useScrollToElementOnLoad } from '@/hooks/useScrollToElementOnLoad';
import { useUserDetails } from '@/hooks/useUserDetails';
import { JourneyEducationTypes } from '@/utils/JourneyEducationTypes';
import {
  getBackgroundImageUrl,
  getBackgroundImageUrlHome,
} from '@/utils/nutrition/getImageUrl';
import { HomeScoresSection } from '@screens/HomePage/ResultReady/HomeScoresSection';
import { HomeStorifyComponent } from '@screens/HomePage/ResultReady/HomeStorifyComponent';
import { QuickActions } from '@screens/HomePage/ResultReady/QuickActions';
import { TrustpilotReviewPopup } from '@screens/HomePage/components/TrustpilotReviewPopup';
import { WrappedCard } from '@screens/HomePage/components/WrappedCard';
import { useWrappedCardVisibility } from '@screens/HomePage/components/useWrappedCardVisibility';
import { useRef } from 'react';
import EducationVideoCarousel from '../EducationVideoCarousel/EducationVideoCarousel';
import { HomeActivateJourney } from '../HomeActivateJourney';
import { HomeTrackProgress } from '../HomeTrackProgress';
import { FeatureFoodCard } from './FeatureFoodCard';

export const ResultsReady = () => {
  const { bootupState } = useBootup();
  const userDetails = useUserDetails();
  const { firstName } = userDetails;
  const communitySectionRef = useRef<HTMLDivElement>(null);
  const isResultsReleasedRecently = isRecentlyPublished(
    bootupState?.rnrLastGeneratedDate,
  );
  const isReTestProcessing = bootupState?.kitDetailsData?.kitReIssued;
  const kitType = bootupState?.kitDetailsData?.kitType;
  const { visible: showWrappedCard, isLoading: isWrappedCardLoading } =
    useWrappedCardVisibility();

  function getHeroSectionBgImg() {
    if (isResultsReleasedRecently) {
      return HOME_POST_RNR_BACKGROUND;
    } else {
      const timeBasedGreeting = getTimeBasedGreetingAndBgImg();
      return isReTestProcessing
        ? timeBasedGreeting.retestBgImg
        : timeBasedGreeting.bgImg;
    }
  }

  const NAVBAR_OFFSET = 70;

  useScrollToElementOnLoad({
    targetId: 'community',
    targetRef: communitySectionRef,
    loadingStates: [],
    scrollOffset: NAVBAR_OFFSET,
    isEnabled: !!bootupState,
  });

  return (
    <>
      <TrustpilotReviewPopup />
      {/* Home screen - Hero section */}
      <div
        className="relative"
        style={{
          backgroundImage: `url(${getBackgroundImageUrl(getHeroSectionBgImg())})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="home-heroSection"
      >
        <div className="page-container">
          <div className="flex justify-between">
            <Render when={firstName}>
              <HomeGreeting userName={firstName} className="text-white" />
            </Render>
            <div className="mr-6 md:mr-0">
              <NotificationCenter screen="rnr" />
            </div>
          </div>
          <HomeStorifyComponent />
          {isResultsReleasedRecently ? (
            <div
              className="pt-6"
              data-testid="home-heroSection-healthResultCard"
            >
              {isWrappedCardLoading ? (
                <Skeleton className="h-48 w-full rounded-2xl" />
              ) : showWrappedCard ? (
                <WrappedCard isNewlyReleased={isResultsReleasedRecently} />
              ) : (
                <HealthResultCard kitType={kitType || undefined} />
              )}
            </div>
          ) : (
            <>
              {/* Home screen - FeaturedFoodCondition */}
              {!isReTestProcessing &&
                bootupState?.kitDetailsData?.kitType !== 'OHI' && (
                  <div
                    className="pt-12"
                    data-testid="home-heroSection-featuredFood"
                  >
                    <FeatureFoodCard />
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      {/* Home screen - Retest users result tracking */}
      <Render when={isReTestProcessing}>
        <div className="bg-[#E4ECF4]" data-testid="home-retestSection">
          <div className="page-container">
            <HomeActivateJourney screen="nornr" screenType="retest" />
            <div className="pt-8">
              <HomeTrackProgress screen="nornr" />
            </div>
          </div>
        </div>
      </Render>

      {(isWrappedCardLoading || showWrappedCard) &&
        !isResultsReleasedRecently && (
          <div className="bg-[#E4ECF4]" data-testid="home-wrappedSection">
            <div className="page-container !py-6">
              {isWrappedCardLoading ? (
                <Skeleton className="h-48 w-full rounded-2xl" />
              ) : (
                <WrappedCard />
              )}
            </div>
          </div>
        )}

      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
        data-testid="home-scoresSection"
      >
        <div className="page-container !pt-12 lg:!pt-[72px]">
          <HomeScoresSection />
          <QuickActions kitType={kitType ?? undefined} />
        </div>
      </div>

      <div data-testid="home-myRecipesSection">
        <MyRecipesSection className="bg-[#E4ECF4]" />
      </div>

      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
        data-testid="home-myNutritionSection"
      >
        <div className="page-container bg-[#E4ECF4]">
          <MyNutritionCard />
        </div>
      </div>

      <section
        className="grid grid-cols-1 !py-6 bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url(${getBackgroundImageUrlHome('formula_bg_large.png')})`,
        }}
        data-testid="home-formulasSection"
      >
        <HomePageFormulaSection />
      </section>

      {/* Home screen - HealthSummary horizntal flow */}
      <div
        className="relative transition-all duration-500 bg-[#E4ECF4]"
        data-testid="home-healthSummarySection"
      >
        <div className="page-container grid grid-cols-1 !pt-6 !pb-6">
          <HealthSummaryCards />
        </div>
      </div>

      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
        data-testid="home-nutritionEssentialsSection"
      >
        <div className="page-container bg-[#E4ECF4] grid grid-cols-1 !pt-6 !pb-6">
          <NutritionEssentials />
        </div>
      </div>

      <div
        className="grid grid-cols-1 bg-[#E4ECF4] -mt-8"
        data-testid="home-educationSection"
      >
        <div className="page-container">
          <EducationVideoCarousel screen="home" />
        </div>
      </div>

      <div ref={communitySectionRef}>
        <section
          className="relative transition-all duration-500"
          style={{
            background: '#E4ECF4',
          }}
          data-testid="home-communitySection"
        >
          <div className="page-container">
            <div
              className="flex flex-col lg:flex-row justify-center gap-2"
              data-testid="home-communitySection-cards"
            >
              <CommunityCard
                backgroundImage={COMMUNITY_CARD_BACKGROUND}
                title="Join the Community"
                description="Join the conversation and connect with fellow wellness enthusiasts on a journey to improve their health."
                buttonText="Join now"
                externalLink={linkoutConfigs.community}
                eventName="home_join_the_community"
                data-testid="home-communitySection-joinCard"
              />
              <CommunityCard
                backgroundImage={REFERAL_CARD_BACKGROUND}
                title="Give $50, Get $50"
                description="When you refer a friend to join the Viome community, you'll both benefit."
                buttonText="Refer a friend"
                externalLink={linkoutConfigs.referAFriend}
                eventName="home_refer_a_friend"
                data-testid="home-communitySection-referCard"
              />
            </div>
            <div
              className="pb-20"
              data-testid="home-communitySection-journeyDeeper"
            >
              {bootupState != null && (
                <NutritionJourneyDeeperSection
                  sectionInfo={
                    isReTestProcessing
                      ? JourneyEducationTypes.HOME_POST_RNR_RETEST
                      : JourneyEducationTypes.HOME_POST_RNR
                  }
                  screen="home"
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
